import { DataBlockVariant, useDataBlockType } from '@lmt/shared-types';
import {
  DataBlockUiEditor,
  DataBlockUiEditorProps,
} from '@lmt/unicorn-shared/app/components/measurements/DataBlockUiEditor';
import {
  addLogoutHandler,
  useClaims,
} from '@lmt/unicorn-shared/app/features/auth/operations';
import clsx from 'clsx';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonColor } from '../common/ui-kit/buttons/Button';
import { FormComponentProps } from '../global-forms/GlobalForms.types';
import { caseInformationDataBlockTypeName } from './common';

import styles from '@lmt/unicorn-shared/app/components/global-forms/Content.module.scss';
import localStyles from './DataBlockStatistic.module.scss';
import { useEventCallback } from '@mui/material';
import {
  getDataBlockValuesByFormId,
  getShowDetailsFromState,
  saveUnsavedDataBlockValuesFromFormToState,
} from '../../features/unsaved-changes/operations';
import { useUniDispatch } from '../../store/types';

export type DataBlockStatisticProps = FormComponentProps & {
  dataBlockId: number;
  dataBlockTypeId: number;
};

export const DataBlockStatistic: FC<DataBlockStatisticProps> = (props) => {
  const {
    formType,
    formId,
    tryCompleteForm,
    dataBlockId,
    dataBlockTypeId,
    loadBlocking,
    registerFormDirtyStateRetriever,
  } = props;

  const i18next = useTranslation();
  const claims = useClaims();
  const dispatch = useUniDispatch();
  const hasUnsavedChangesRef = useRef(false);
  const selectedDataBlockType = useDataBlockType(dataBlockTypeId);
  const [isCopyFieldsEnabled, setCopyFieldsEnabled] = useState<boolean>(false);

  useEffect(() => {
    registerFormDirtyStateRetriever(formId, () => hasUnsavedChangesRef.current);
  }, []);
  const setHasUnsavedChanges = useEventCallback((value: boolean) => {
    hasUnsavedChangesRef.current = value;
  });

  const actions: DataBlockUiEditorProps['actions'] = useRef(null);

  const onCancel = useEventCallback(async () => {
    await loadBlocking(formId, i18next.t('data_blocks.loading'), async () => {
      await tryCompleteForm(formType, formId, async () => undefined);
    });
  });

  const onSave = useEventCallback(async () => {
    await loadBlocking(
      formId,
      i18next.t('data_blocks.loading_save'),
      async () => {
        await tryCompleteForm(formType, formId, async () => {
          await actions.current?.save();
        }).catch((e) => console.error('DataBlockStatistic.onSave', e));
      },
    );
  });

  // set default values
  const setUnsavedValues = useCallback(() => {
    const defaultDataBlockValues = getDataBlockValuesByFormId(formId);
    if (defaultDataBlockValues) {
      actions.current?.setUnsavedValues(defaultDataBlockValues);
      actions.current?.setShowDetails(getShowDetailsFromState(formId));
    } else {
      setCopyFieldsEnabled(true);
    }
  }, [actions]);

  useEffect(() => {
    setUnsavedValues();
  }, []);

  useEffect(() => {
    return addLogoutHandler(() => {
      const currentValues = actions.current?.getUnsavedValues();
      if (currentValues) {
        const isShowDetails = actions.current?.getShowDetails() ?? false;
        saveUnsavedDataBlockValuesFromFormToState(
          formId,
          currentValues,
          isShowDetails,
          dispatch,
        );
      }
    });
  }, []);

  return (
    <div
      className={clsx(styles.autoSizedContainer, localStyles.container)}
      data-test-id="dataBlockViewPopup"
    >
      <div
        className={clsx(styles.dataContainer, localStyles['data-container'])}
      >
        <DataBlockUiEditor
          disableCopyFromPreviousFields={!isCopyFieldsEnabled}
          dataBlockTypeId={props.dataBlockTypeId}
          dataBlockId={dataBlockId}
          type={'update_data_block'}
          isEditable={claims.permissions.dataBlockManagement}
          actions={actions}
          isStatistic={true}
          setUnsavedChanges={setHasUnsavedChanges}
        />
      </div>
      {claims.permissions.dataBlockManagement && (
        <div className={styles.submitContainer}>
          {(dataBlockId ||
            selectedDataBlockType?.variant === DataBlockVariant.Patient) && (
            <>
              <Button
                color={ButtonColor.Primary}
                disabled={
                  selectedDataBlockType?.variant === DataBlockVariant.Case &&
                  selectedDataBlockType?.value ===
                    caseInformationDataBlockTypeName
                }
                onClick={onCancel}
                title={i18next.t('data_blocks.cancel_button')}
                className={styles.submit}
              />

              <Button
                onClick={onSave}
                title={i18next.t('data_blocks.save_button')}
                className={styles.submit}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DataBlockStatistic;
